// @ts-ignore
import css from "./widget.css";
import { getCurrentDevice } from "@/utils/device";
import { Accessibility } from "@/icons/accessibility";
import { Assist } from "@/icons/assist";
import { DefaultIcon } from "@/icons/default";
import { wheel } from "@/icons/wheel";
import type { WidgetConfig } from "@/types/widget";

export interface RenderButtonOptions {
  config: WidgetConfig;
  label: string;
  onActivate: () => void;
}

export const ICON_LIBRARY = [
  { id: "default", icon: DefaultIcon },
  { id: "accessibility", icon: Accessibility },
  { id: "assist", icon: Assist },
  { id: "wheel", icon: wheel },
] as const;

/** Cross-bundle accessor: loader renders the button, core must find it. */
export function getWidgetEl(): HTMLElement | null {
  return (
    document.querySelector("#cya11y-container")?.shadowRoot
      ?.querySelector(".cya11y-widget") as HTMLElement | null
  ) ?? null;
}

function hexToRgb(hex: string): [number, number, number] | null {
  const cleaned = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;
  return [
    parseInt(cleaned.slice(0, 2), 16),
    parseInt(cleaned.slice(2, 4), 16),
    parseInt(cleaned.slice(4, 6), 16),
  ];
}

function getRelativeLuminance(r: number, g: number, b: number): number {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function getContrastRatio(hex1: string, hex2: string): number {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return 1;
  const l1 = getRelativeLuminance(...rgb1);
  const l2 = getRelativeLuminance(...rgb2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

const POSITION_STYLES = {
  "bottom-right": (m: any) => ({ bottom: `${m.bottom}px`, right: `${m.right}px`, left: "auto" }),
  "top-left": (m: any) => ({ top: `${m.top}px`, left: `${m.left}px`, bottom: "auto", right: "auto" }),
  "top-right": (m: any) => ({ top: `${m.top}px`, right: `${m.right}px`, left: "auto", bottom: "auto" }),
  "bottom-left": (m: any) => ({ bottom: `${m.bottom}px`, left: `${m.left}px`, right: "auto" }),
} as const;

function getButtonStyle(config: WidgetConfig): Record<string, string> {
  const device = getCurrentDevice();
  const currentPosition = config.position[device];
  const currentMargins = config.margins[device];
  const positionStyle = POSITION_STYLES[currentPosition as keyof typeof POSITION_STYLES];
  return positionStyle ? positionStyle(currentMargins) : POSITION_STYLES["bottom-left"](currentMargins);
}

function handleButtonKeydown(event: KeyboardEvent, button: HTMLElement, onActivate: () => void): void {
  switch (event.key) {
    case "Enter":
    case " ":
      event.preventDefault();
      onActivate();
      break;
    case "Escape": {
      // Menu-close on Escape: DOM query at event time, so it works even though
      // the menu is rendered by the (separately bundled) core after this
      // listener was attached.
      const shadowRoot = document.querySelector("#cya11y-container")?.shadowRoot;
      const menu = shadowRoot?.querySelector(".cya11y-menu") as HTMLElement | null;
      if (menu && menu.style.display !== "none") {
        (menu.querySelector(".cya11y-menu-close") as HTMLElement | null)?.click();
        button.setAttribute("aria-expanded", "false");
      }
      break;
    }
  }
}

export function renderButton({ config, label, onActivate }: RenderButtonOptions): HTMLElement | null {
  try {
    const device = getCurrentDevice();
    const deviceStatus = device === "mobile" ? config.status.mobile : config.status.desktop;
    if (!deviceStatus) return null;

    const $container: HTMLElement = document.createElement("div");
    $container.id = "cya11y-container";
    const shadow = $container.attachShadow({ mode: "open" });

    const currentPosition = config.position[device];
    const selectedIcon = ICON_LIBRARY.find((icon) => icon.id === config.iconId);

    shadow.innerHTML = `
      <style id="cya11y-widget-css">${css}</style>
      <div class="cya11y-widget" data-position="${currentPosition}">
        <div class="cya11y-widget-icon">
          <button
             class="cya11y-menu-btn"
             aria-label="${label}"
             title="${label}"
             aria-expanded="false"
             aria-haspopup="dialog"
             role="button"
             type="button"
             tabindex="0">
            <span class="cya11y-btn-content" aria-hidden="true">
              ${selectedIcon?.icon || DefaultIcon}
            </span>
            <span class="cya11y-sr-only" aria-hidden="true">Open accessibility menu</span>
          </button>
        </div>
      </div>
    `;

    const $widget = shadow.querySelector(".cya11y-widget") as HTMLElement;
    if (!$widget) throw new Error("Failed to find widget element");

    $widget.style.setProperty("--cya11y-primary-color", config.primaryColor);
    $widget.style.setProperty("--cya11y-size", `${config.iconSize}px`);

    const contrastWithWhite = getContrastRatio(config.primaryColor, "#ffffff");
    const primaryVisibleOnWhite = contrastWithWhite >= 3;
    const customFocusRing = config.focusRingColor;

    $widget.style.setProperty(
      "--cya11y-focus-color-on-primary",
      customFocusRing || (primaryVisibleOnWhite ? "#ffffff" : "#000000")
    );
    $widget.style.setProperty(
      "--cya11y-focus-color-on-white",
      primaryVisibleOnWhite ? config.primaryColor : "#000000"
    );
    const focusRingColor = customFocusRing || (primaryVisibleOnWhite ? config.primaryColor : "#000000");
    $widget.style.setProperty("--cya11y-focus-ring-color", focusRingColor);

    const shadowSourceColor = customFocusRing || config.primaryColor;
    const rgb = hexToRgb(shadowSourceColor);
    const shadowColor = rgb && (customFocusRing || primaryVisibleOnWhite)
      ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.14)`
      : "rgba(0, 0, 0, 0.14)";
    $widget.style.setProperty("--cya11y-shadow-focus", `0px 0px 0px 6px ${shadowColor}`);

    const $btn = $widget.querySelector(".cya11y-menu-btn") as HTMLElement;
    const $icon = $widget.querySelector(".cya11y-widget-icon") as HTMLElement;

    if ($btn && $icon) {
      Object.assign($btn.style, getButtonStyle(config));
      Object.assign($icon.style, getButtonStyle(config));

      $btn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        onActivate();
        setTimeout(() => {
          const shadowRoot = document.querySelector("#cya11y-container")?.shadowRoot;
          const menu = shadowRoot?.querySelector(".cya11y-menu") as HTMLElement | null;
          if (menu) $btn.setAttribute("aria-expanded", (menu.style.display !== "none").toString());
        }, 10);
      });

      $btn.addEventListener("keydown", (event) => handleButtonKeydown(event, $btn, onActivate));
      $btn.addEventListener("focus", () => $btn.classList.add("cya11y-focus-visible"));
      $btn.addEventListener("blur", () => $btn.classList.remove("cya11y-focus-visible"));
    }

    document.body.insertBefore($container, document.body.firstChild);
    return $widget;
  } catch (error) {
    console.error("Failed to render widget button:", error);
    return null;
  }
}
